import { BoardType } from "@sportactics/board-editing";
import { useSWRConfig } from "swr";

import { AnalyticsEventType, useAnalyticsEvent } from "../analytics-event";
import { useFirebaseAuth } from "../user";
import { useBoardTypeFirestorePersistence } from "./use-board-type-firestore-persistence";
import { getBoardTypeFirestoreSWRKey } from "./use-board-type-firestore-swr";
import { useBoardTypeLocalPersistence } from "./use-board-type-local-persistence";
import { useBoardTypeLocalSWR } from "./use-board-type-local-swr";

// No context: every caller reads the same SWR key, so the selected board type is
// already shared across the tree without a provider to hold it.
//
// The read is local for signed-in users too: the Firestore copy reaches this device
// through `useBoardTypeSyncEffect`, so no board-type Firestore read sits on the board's
// critical path — which matters because onboarding and the editor are both gated on the
// board type. Identity comes from `useFirebaseAuth` rather than `useUser` for the same
// reason, though `useAnalyticsEvent` below still reaches `useUser`, so the user document
// remains on this hook's path until that changes.
export function useSelectBoardType(): useSelectBoardTypeReturn {
  const { data, mutate: mutateLocal } = useBoardTypeLocalSWR();
  const { setBoardType: setLocalBoardType } = useBoardTypeLocalPersistence();
  const { setBoardType: setFirestoreBoardType } =
    useBoardTypeFirestorePersistence();
  const { userId, authenticated } = useFirebaseAuth();
  const { mutate } = useSWRConfig();
  const { submitEvent } = useAnalyticsEvent();

  const boardType = data ?? undefined;

  const selectBoardType = async (newBoardType: BoardType): Promise<void> => {
    // Firestore is written inside the local mutation and before the local copy, as in
    // `useEditPreferences`: a rejected Firestore write then leaves both untouched,
    // where writing them concurrently would roll the cache back to the previous board
    // type while localForage already held the new one.
    //
    // The Firestore key is mutated through the global `mutate` rather than by
    // subscribing to it — the one departure from `useEditPreferences` — because
    // subscribing would make every consumer of the selected board type suspend on the
    // Firestore read this hook exists to stay off.
    await mutateLocal(
      async () => {
        if (authenticated)
          await mutate(
            getBoardTypeFirestoreSWRKey(userId),
            setFirestoreBoardType(userId, newBoardType).then(
              () => newBoardType,
            ),
            { optimisticData: newBoardType, revalidate: false },
          );

        await setLocalBoardType(newBoardType);
        return newBoardType;
      },
      { optimisticData: newBoardType, revalidate: false },
    );

    submitEvent({
      type: AnalyticsEventType.SelectBoardType,
      boardType: newBoardType,
    });
  };

  return { boardType, selectBoardType };
}

// `boardType` stays optional: the persisted read resolves to undefined when nothing
// has been selected yet. There is no loading flag, because suspending means the read
// has always resolved by the time this returns.
export type useSelectBoardTypeReturn = {
  boardType?: BoardType;
  selectBoardType: (boardType: BoardType) => Promise<void>;
};
